import { Helmet } from 'react-helmet-async';

const SITE_URL   = 'https://themeatworks.com.au';
const SITE_NAME  = 'The Meat Works';
const OG_IMAGE   = `${SITE_URL}/og-image.jpg`;
const TWITTER    = '@themeatworks';    // update when a handle exists

/**
 * Per-page SEO wrapper. Pass a `path` (e.g. "/export") to build the canonical
 * URL; leave it undefined for the homepage.
 *
 * `jsonLd` may be a single object or an array — each is stringified into its
 * own <script type="application/ld+json"> so it can be validated by Google's
 * Rich Results test.
 *
 * `noindex` sets robots to noindex,nofollow — use for private routes
 * (account, checkout success/cancel, admin).
 */
export default function Seo({
  title,
  description,
  path = '/',
  image = OG_IMAGE,
  jsonLd,
  noindex = false,
  keywords,
}) {
  const canonical = `${SITE_URL}${path === '/' ? '/' : path}`;
  const fullTitle = title
    ? `${title} · ${SITE_NAME}`
    : `${SITE_NAME} · Premium Wholesale Butchery · Malaga WA`;
  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'}
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_AU" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD structured data */}
      {jsonLdArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
